import { useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  BoltIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { useWorkspace } from '../context/WorkspaceContext';
import type { TeamRole } from '../lib/types';
import {
  formatRelativeTime,
  getBuildReviewMetrics,
  getBuildReviewState,
  getRoleActionHints,
  markRunStatus,
  promoteQAItem,
  updateReviewStatus,
  updateSelectedBuildReviewRole,
  type BuildRunStatus,
  type QASeverity,
  type ReviewStatus,
} from '../lib/services/build-review.service';

const ROLE_OPTIONS: TeamRole[] = ['team_lead', 'developer', 'planner', 'qa'];
const REVIEW_STATUSES: ReviewStatus[] = ['draft', 'review', 'approved', 'fix_required', 'retest'];

export function BuildReviewPage() {
  const { currentWorkspace } = useWorkspace();
  const [version, setVersion] = useState(0);

  const state = useMemo(() => getBuildReviewState(currentWorkspace?.id), [currentWorkspace?.id, version]);
  const metrics = getBuildReviewMetrics(state);
  const roleHint = getRoleActionHints(state.selectedRole);

  if (!currentWorkspace) return null;

  const refresh = () => setVersion((value) => value + 1);

  return (
    <div className="p-6 space-y-6">
      <section className="card overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-primary-900 text-white border-0">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-primary-100">
              <ShieldCheckIcon className="h-4 w-4" />
              Build Review
            </div>
            <h1 className="mt-4 text-3xl font-bold">Test → Review → Approve → Reflect</h1>
            <p className="mt-2 text-sm text-slate-200">
              Keep releases visible inside Vice with one operating surface for recent runs, QA inbox,
              review decisions, and Linear-linked follow-through.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 lg:w-[420px]">
            <MetricCard label="Active runs" value={String(metrics.activeRuns)} tone="blue" />
            <MetricCard label="Blockers" value={String(metrics.blockers)} tone="red" />
            <MetricCard label="Approved" value={String(metrics.approvals)} tone="green" />
            <MetricCard label="Linear sync" value={metrics.syncCoverage} tone="amber" />
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-[1.7fr_1fr]">
        <div className="space-y-6">
          <BuildRunsPanel
            runs={state.runs}
            onStatusChange={(runId, status) => {
              markRunStatus(runId, status);
              refresh();
            }}
          />
          <QAInboxPanel
            items={state.qaInbox}
            onPromote={(itemId) => {
              promoteQAItem(itemId);
              refresh();
            }}
          />
          <ReviewBoardPanel
            reviews={state.reviews}
            onStatusChange={(reviewId, status) => {
              updateReviewStatus(reviewId, status);
              refresh();
            }}
          />
        </div>

        <div className="space-y-6">
          <RoleHintPanel
            selectedRole={state.selectedRole}
            onRoleChange={(role) => {
              updateSelectedBuildReviewRole(role);
              refresh();
            }}
            roleHint={roleHint}
          />
          <LinearSyncPanel syncItems={state.linearSync} />
          <FlowExplainerPanel />
        </div>
      </section>
    </div>
  );
}

function MetricCard({ label, value, tone }: { label: string; value: string; tone: 'blue' | 'red' | 'green' | 'amber' }) {
  const toneClass = {
    blue: 'bg-blue-500/15 text-blue-100 border-blue-300/20',
    red: 'bg-red-500/15 text-red-100 border-red-300/20',
    green: 'bg-green-500/15 text-green-100 border-green-300/20',
    amber: 'bg-amber-500/15 text-amber-100 border-amber-300/20',
  }[tone];

  return (
    <div className={clsx('rounded-2xl border p-4 backdrop-blur-sm', toneClass)}>
      <p className="text-xs uppercase tracking-wide text-white/70">{label}</p>
      <p className="mt-2 text-2xl font-semibold text-white">{value}</p>
    </div>
  );
}

function BuildRunsPanel({ runs, onStatusChange }: { runs: ReturnType<typeof getBuildReviewState>['runs']; onStatusChange: (runId: string, status: BuildRunStatus) => void }) {
  return (
    <section className="card">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Build Runs / Recent Runs</h2>
          <p className="text-sm text-gray-500 mt-1">Latest build, test, and deploy attempts with fast recovery actions.</p>
        </div>
        <span className="badge badge-info">
          <ArrowTrendingUpIcon className="h-3.5 w-3.5 mr-1" />
          Live demo state
        </span>
      </div>
      <div className="space-y-3">
        {runs.map((run) => (
          <div key={run.id} className="rounded-xl border border-gray-200 p-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={clsx('badge', run.status === 'passed' ? 'badge-success' : run.status === 'failed' ? 'badge-danger' : run.status === 'running' ? 'badge-info' : 'badge-warning')}>
                    {run.stage}
                  </span>
                  <span className="text-sm font-semibold text-gray-900">{run.label}</span>
                  <span className="text-xs text-gray-400 font-mono">{run.commit}</span>
                </div>
                <p className="text-sm text-gray-600 mt-2">{run.summary}</p>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                  <span>Branch: <span className="font-medium text-gray-700">{run.branch}</span></span>
                  <span>Owner: <span className="font-medium text-gray-700">{run.actor}</span></span>
                  <span>Duration: <span className="font-medium text-gray-700">{run.duration}</span></span>
                  <span>Updated {formatRelativeTime(run.finishedAt)}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button className="btn-secondary btn-sm" onClick={() => onStatusChange(run.id, 'failed')}>Mark failed</button>
                <button className="btn-primary btn-sm" onClick={() => onStatusChange(run.id, 'passed')}>Mark passed</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function QAInboxPanel({ items, onPromote }: { items: ReturnType<typeof getBuildReviewState>['qaInbox']; onPromote: (itemId: string) => void }) {
  return (
    <section className="card">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">QA Inbox</h2>
          <p className="text-sm text-gray-500 mt-1">Failures, cautions, and improvement ideas waiting to be reviewed.</p>
        </div>
        <span className="badge badge-gray">{items.length} open items</span>
      </div>
      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} className="rounded-xl border border-gray-200 p-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <SeverityBadge severity={item.severity} />
                  <span className="text-sm font-semibold text-gray-900">{item.title}</span>
                </div>
                <p className="text-sm text-gray-600 mt-2">{item.summary}</p>
                <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-500">
                  <span className="tag">{item.area}</span>
                  <span className="tag">source: {item.source}</span>
                  <span className="tag">owner role: {item.ownerRole}</span>
                  <span className="tag">run: {item.linkedRunId}</span>
                  <span className="tag">issue: {item.linkedIssueId}</span>
                </div>
              </div>
              <button className="btn-primary btn-sm" onClick={() => onPromote(item.id)}>
                Promote to review
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ReviewBoardPanel({ reviews, onStatusChange }: { reviews: ReturnType<typeof getBuildReviewState>['reviews']; onStatusChange: (reviewId: string, status: ReviewStatus) => void }) {
  const columns: ReviewStatus[] = ['draft', 'review', 'approved', 'fix_required', 'retest'];

  return (
    <section className="card">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Review Board</h2>
          <p className="text-sm text-gray-500 mt-1">Move work through draft, review, approval, fix, and retest without leaving Vice.</p>
        </div>
        <span className="badge badge-purple">
          <BoltIcon className="h-3.5 w-3.5 mr-1" />
          Stateful board
        </span>
      </div>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
        {columns.map((column) => {
          const cards = reviews.filter((review) => review.status === column);
          return (
            <div key={column} className="pipeline-column min-h-[260px]">
              <div className="pipeline-header">
                <div className="pipeline-header-title">
                  <span className="capitalize">{column.replace('_', ' ')}</span>
                </div>
                <span className="pipeline-header-count">{cards.length}</span>
              </div>
              <div className="space-y-3">
                {cards.map((review) => (
                  <div key={review.id} className="pipeline-item !mb-0">
                    <p className="text-sm font-semibold text-gray-900">{review.title}</p>
                    <p className="text-xs text-gray-500 mt-2">{review.scope}</p>
                    <div className="mt-3 space-y-1 text-xs text-gray-500">
                      <p>Owner: <span className="text-gray-700 font-medium">{review.owner}</span></p>
                      <p>Reviewer: <span className="text-gray-700 font-medium">{review.reviewer}</span></p>
                      <p>Issue: <span className="font-mono text-gray-700">{review.linkedIssueId}</span></p>
                      <p>Updated {formatRelativeTime(review.updatedAt)}</p>
                    </div>
                    <select
                      className="select mt-3"
                      value={review.status}
                      onChange={(event) => onStatusChange(review.id, event.target.value as ReviewStatus)}
                    >
                      {REVIEW_STATUSES.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function RoleHintPanel({
  selectedRole,
  onRoleChange,
  roleHint,
}: {
  selectedRole: TeamRole;
  onRoleChange: (role: TeamRole) => void;
  roleHint: ReturnType<typeof getRoleActionHints>;
}) {
  return (
    <section className="card">
      <div className="flex items-center gap-2 mb-4">
        <CheckCircleIcon className="h-5 w-5 text-primary-600" />
        <h2 className="text-lg font-semibold text-gray-900">Role-based view hint</h2>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {ROLE_OPTIONS.map((role) => (
          <button
            key={role}
            onClick={() => onRoleChange(role)}
            className={clsx(
              'rounded-xl border px-3 py-2 text-left text-sm transition-colors',
              selectedRole === role
                ? 'border-primary-300 bg-primary-50 text-primary-700'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
            )}
          >
            <p className="font-semibold capitalize">{role.replace('_', ' ')}</p>
          </button>
        ))}
      </div>
      <div className="mt-4 rounded-xl bg-gray-50 p-4">
        <p className="text-sm font-medium text-gray-900">{roleHint.title}</p>
        <ul className="mt-3 space-y-2 text-sm text-gray-600">
          {roleHint.actions.map((action) => (
            <li key={action} className="flex items-start gap-2">
              <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary-500" />
              <span>{action}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function LinearSyncPanel({ syncItems }: { syncItems: ReturnType<typeof getBuildReviewState>['linearSync'] }) {
  return (
    <section className="card">
      <div className="flex items-center gap-2 mb-4">
        <LinkIcon className="h-5 w-5 text-indigo-500" />
        <h2 className="text-lg font-semibold text-gray-900">Linear Sync</h2>
      </div>
      <div className="space-y-3">
        {syncItems.map((item) => (
          <div key={item.id} className="rounded-xl border border-gray-200 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-gray-500">{item.issueId}</span>
                  <span className="badge badge-info">{item.state}</span>
                </div>
                <p className="mt-2 text-sm font-semibold text-gray-900">{item.title}</p>
                <div className="mt-3 space-y-1 text-xs text-gray-500">
                  <p>Owner: <span className="font-medium text-gray-700">{item.owner}</span></p>
                  <p>Branch: <span className="font-mono text-gray-700">{item.branch}</span></p>
                  <p>Last sync: {formatRelativeTime(item.syncedAt)}</p>
                </div>
              </div>
              <ArrowPathIcon className="h-5 w-5 text-gray-300" />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FlowExplainerPanel() {
  return (
    <section className="card">
      <div className="flex items-center gap-2 mb-4">
        <ClockIcon className="h-5 w-5 text-amber-500" />
        <h2 className="text-lg font-semibold text-gray-900">Loop flow</h2>
      </div>
      <div className="space-y-3 text-sm text-gray-600">
        <div className="rounded-xl bg-gray-50 p-4">
          <p className="font-medium text-gray-900">1. Test</p>
          <p className="mt-1">Recent runs make failed build/test/deploy attempts visible immediately.</p>
        </div>
        <div className="rounded-xl bg-gray-50 p-4">
          <p className="font-medium text-gray-900">2. Review</p>
          <p className="mt-1">QA Inbox turns evidence into review-ready cards with severity and linked issues.</p>
        </div>
        <div className="rounded-xl bg-gray-50 p-4">
          <p className="font-medium text-gray-900">3. Approve</p>
          <p className="mt-1">Review Board shows whether work is in draft, review, approved, fix required, or retest.</p>
        </div>
        <div className="rounded-xl bg-gray-50 p-4">
          <p className="font-medium text-gray-900">4. Reflect / Ship</p>
          <p className="mt-1">Linear Sync keeps release follow-through traceable after approval and retest.</p>
        </div>
      </div>
    </section>
  );
}

function SeverityBadge({ severity }: { severity: QASeverity }) {
  const className = severity === 'blocker' ? 'badge-danger' : severity === 'warning' ? 'badge-warning' : 'badge-gray';
  const icon = severity === 'blocker' ? <ExclamationTriangleIcon className="h-3.5 w-3.5 mr-1" /> : severity === 'warning' ? <ClockIcon className="h-3.5 w-3.5 mr-1" /> : <BoltIcon className="h-3.5 w-3.5 mr-1" />;

  return <span className={clsx('badge', className)}>{icon}{severity}</span>;
}

export default BuildReviewPage;
